import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader, Target, Check, ThumbsUp, ThumbsDown, RefreshCw, Upload, Link, Trash2, Image } from 'lucide-react';
import { extractBrandFromSource } from '../../services/aiService';
import { uploadBrandLogo } from '../../services/brandService';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useApp } from '../../contexts/AppContext';
import ConfirmationModal from '../ConfirmationModal';

const initialFormData = {
    name: '',
    website: '',
    logo: '',
    industry: '',
    products: '',
    voice: {
        tone: '',
        personality: '',
    },
    visual: {
        primaryColor: '#7C3AED',
        secondaryColor: '#06B6D4',
        accentColor: '#EC4899',
    },
    audience: {
        demographics: '',
        interests: '',
        painPoints: '',
    },
};

const BrandEditorSidebar = ({ isOpen, onClose, onSave, editingBrand = null }) => {
    const { user } = useApp();
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [researchingAudience, setResearchingAudience] = useState(false);
    const [showFeedbackInput, setShowFeedbackInput] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [audienceResearched, setAudienceResearched] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [logoInputMode, setLogoInputMode] = useState('url'); // 'url' or 'upload'
    const [logoUrlInput, setLogoUrlInput] = useState('');
    const fileInputRef = useRef(null);

    const {
        captureInitialState,
        showUnsavedModal,
        setShowUnsavedModal,
        handleCloseAttempt
    } = useUnsavedChanges(formData);

    // Initialize form when sidebar opens or editingBrand changes
    useEffect(() => {
        if (isOpen) {
            const data = editingBrand || initialFormData;
            setFormData(data);
            captureInitialState(data);
            setAudienceResearched(false);
            setShowFeedbackInput(false);
            setFeedbackText('');
            setIsClosing(false);
            setLogoUrlInput(data.logo?.startsWith('http') ? data.logo : '');
            setLogoInputMode('url');
        }
    }, [isOpen, editingBrand, captureInitialState]);

    const handleLogoUrlChange = (url) => {
        setLogoUrlInput(url);
    };

    const handleLogoUrlApply = () => {
        if (logoUrlInput.trim()) {
            setFormData({ ...formData, logo: logoUrlInput.trim() });
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const base64 = event.target?.result;
            if (base64) {
                setFormData({ ...formData, logo: base64 });
                setLogoUrlInput('');
            }
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = () => {
        setFormData({ ...formData, logo: '' });
        setLogoUrlInput('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        if (handleCloseAttempt()) {
            return; // Modal will show
        }
        animateClose();
    };

    const animateClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 200);
    };

    const handleDiscardAndClose = () => {
        setShowUnsavedModal(false);
        animateClose();
    };

    const handleAnalyze = async () => {
        if (!formData.website) {
            return;
        }

        setAnalyzing(true);
        try {
            const result = await extractBrandFromSource(formData.website, 'website', null);

            setFormData(prev => ({
                ...prev,
                name: prev.name || result.name || '',
                logo: result.logo || prev.logo,
                industry: result.industry || prev.industry,
                products: result.products || prev.products,
                visual: {
                    primaryColor: result.colors?.primary || prev.visual.primaryColor,
                    secondaryColor: result.colors?.secondary || prev.visual.secondaryColor,
                    accentColor: result.colors?.accent || prev.visual.accentColor,
                },
                voice: {
                    tone: result.voice?.tone || prev.voice.tone,
                    personality: result.voice?.personality || prev.voice.personality,
                },
            }));
        } catch (error) {
            console.error("Analysis failed:", error);
        } finally {
            setAnalyzing(false);
        }
    };

    const handleResearchAudience = async (customFeedback = null) => {
        if (!formData.name || !formData.industry) {
            return;
        }

        setResearchingAudience(true);
        try {
            const response = await fetch('http://localhost:3001/api/research-audience', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    brandName: formData.name,
                    industry: formData.industry,
                    website: formData.website,
                    products: formData.products,
                    feedback: customFeedback,
                }),
            });

            if (response.ok) {
                const audienceData = await response.json();
                setFormData(prev => ({
                    ...prev,
                    audience: {
                        demographics: audienceData.demographics || '',
                        interests: audienceData.interests || '',
                        painPoints: audienceData.painPoints || '',
                    },
                }));
                setAudienceResearched(true);
                setShowFeedbackInput(false);
                setFeedbackText('');
            } else {
                throw new Error('Audience research failed');
            }
        } catch (error) {
            console.error("Audience research failed:", error);
        } finally {
            setResearchingAudience(false);
        }
    };

    const handleAcceptAudience = () => {
        setAudienceResearched(false);
        setShowFeedbackInput(false);
    };

    const handleRequestChanges = () => {
        setShowFeedbackInput(true);
    };

    const handleRegenerateWithFeedback = () => {
        if (feedbackText.trim()) {
            handleResearchAudience(feedbackText);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let logoUrl = formData.logo;

            // If logo is base64 data, upload to Storage first
            if (logoUrl && logoUrl.startsWith('data:')) {
                const brandId = editingBrand?.id || 'new';
                logoUrl = await uploadBrandLogo(logoUrl, user.uid, brandId);
            }

            const brandData = {
                ...formData,
                logo: logoUrl,
                updatedAt: new Date().toISOString(),
            };

            if (editingBrand) {
                await onSave(editingBrand.id, brandData, true);
            } else {
                await onSave(null, { ...brandData, createdAt: new Date().toISOString() }, false);
            }

            animateClose();
        } catch (error) {
            console.error('Error saving brand:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen && !isClosing) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`sidebar-overlay ${isClosing ? 'closing' : ''}`}
                onClick={handleClose}
            />

            {/* Sidebar Panel */}
            <div className={`sidebar-panel ${isClosing ? 'closing' : ''}`}>
                <div className="sidebar-header">
                    <h2>{editingBrand ? 'Edit Brand' : 'Create New Brand'}</h2>
                    <button className="btn-icon btn-ghost" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="sidebar-body">
                        {/* AI-First Input Section */}
                        <div className="card" style={{ background: 'linear-gradient(135deg, var(--color-primary-light), var(--color-secondary-light))', border: '2px solid var(--color-primary)', marginBottom: 'var(--space-6)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                                <Sparkles size={20} className="text-primary" />
                                <h3 style={{ margin: 0 }}>AI Brand Analysis</h3>
                            </div>
                            <p className="text-secondary" style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                                Enter your brand name and website URL. Our AI will automatically extract your brand identity, colors, and voice.
                            </p>

                            <div className="input-group" style={{ marginBottom: 'var(--space-3)' }}>
                                <label className="input-label">Brand Name</label>
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="e.g., Acme Corp"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                                <label className="input-label">Website URL</label>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <input
                                        type="url"
                                        className="input-field"
                                        placeholder="https://www.yourbrand.com"
                                        value={formData.website}
                                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleAnalyze}
                                        disabled={analyzing || !formData.website}
                                    >
                                        {analyzing ? <Loader className="animate-spin" size={16} /> : <><Sparkles size={16} /> Analyze</>}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Brand Logo Section */}
                        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                                <Image size={20} className="text-primary" />
                                <h4 style={{ margin: 0 }}>Brand Logo</h4>
                            </div>

                            {/* Logo Preview */}
                            {formData.logo && (
                                <div style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
                                    <div
                                        style={{
                                            width: '120px',
                                            height: '120px',
                                            margin: '0 auto var(--space-3)',
                                            background: `linear-gradient(135deg, ${formData.visual.primaryColor}, ${formData.visual.secondaryColor})`,
                                            borderRadius: 'var(--radius-xl)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '3rem',
                                            overflow: 'hidden',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            padding: 'var(--space-3)',
                                            position: 'relative'
                                        }}
                                    >
                                        {formData.logo.startsWith('http') || formData.logo.startsWith('data:') ? (
                                            <img src={formData.logo} alt={formData.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            formData.logo
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={handleRemoveLogo}
                                        style={{ gap: 'var(--space-1)' }}
                                    >
                                        <Trash2 size={14} /> Remove Logo
                                    </button>
                                </div>
                            )}

                            {/* Logo Input Mode Tabs */}
                            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${logoInputMode === 'url' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setLogoInputMode('url')}
                                    style={{ flex: 1 }}
                                >
                                    <Link size={14} /> URL
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${logoInputMode === 'upload' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setLogoInputMode('upload')}
                                    style={{ flex: 1 }}
                                >
                                    <Upload size={14} /> Upload
                                </button>
                            </div>

                            {/* URL Input */}
                            {logoInputMode === 'url' && (
                                <div className="input-group" style={{ marginBottom: 0 }}>
                                    <label className="input-label">Logo URL</label>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                        <input
                                            type="url"
                                            className="input-field"
                                            placeholder="https://example.com/logo.png"
                                            value={logoUrlInput}
                                            onChange={(e) => handleLogoUrlChange(e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleLogoUrlApply}
                                            disabled={!logoUrlInput.trim()}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* File Upload */}
                            {logoInputMode === 'upload' && (
                                <div className="input-group" style={{ marginBottom: 0 }}>
                                    <label className="input-label">Upload Image</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        <Upload size={16} /> Choose File
                                    </button>
                                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)', marginBottom: 0 }}>
                                        PNG, JPG, or SVG. Max 2MB.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Brand Identity */}
                        <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                            <label className="input-label">Industry</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="e.g., E-commerce, SaaS, Healthcare"
                                value={formData.industry}
                                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                            />
                        </div>

                        <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                            <label className="input-label">Products / Services</label>
                            <textarea
                                className="input-field"
                                placeholder="e.g., E-commerce platform for small businesses"
                                value={formData.products}
                                onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                                rows={2}
                            />
                        </div>

                        {/* Brand Voice */}
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h4 style={{ marginBottom: 'var(--space-3)' }}>Brand Voice</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="input-group">
                                    <label className="input-label">Tone</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="e.g., Professional, Friendly"
                                        value={formData.voice.tone}
                                        onChange={(e) => setFormData({ ...formData, voice: { ...formData.voice, tone: e.target.value } })}
                                    />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Personality</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="e.g., Innovative, Trustworthy"
                                        value={formData.voice.personality}
                                        onChange={(e) => setFormData({ ...formData, voice: { ...formData.voice, personality: e.target.value } })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Brand Colors */}
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h4 style={{ marginBottom: 'var(--space-3)' }}>Brand Colors</h4>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="input-group">
                                    <label className="input-label">Primary</label>
                                    <input
                                        type="color"
                                        className="input-field"
                                        value={formData.visual.primaryColor}
                                        onChange={(e) => setFormData({ ...formData, visual: { ...formData.visual, primaryColor: e.target.value } })}
                                        style={{ height: '48px', cursor: 'pointer' }}
                                    />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Secondary</label>
                                    <input
                                        type="color"
                                        className="input-field"
                                        value={formData.visual.secondaryColor}
                                        onChange={(e) => setFormData({ ...formData, visual: { ...formData.visual, secondaryColor: e.target.value } })}
                                        style={{ height: '48px', cursor: 'pointer' }}
                                    />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Accent</label>
                                    <input
                                        type="color"
                                        className="input-field"
                                        value={formData.visual.accentColor}
                                        onChange={(e) => setFormData({ ...formData, visual: { ...formData.visual, accentColor: e.target.value } })}
                                        style={{ height: '48px', cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Target Audience Research */}
                        <div className="card" style={{ background: 'var(--color-bg-secondary)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <Target size={20} className="text-primary" />
                                    <h4 style={{ margin: 0 }}>Target Audience</h4>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => handleResearchAudience()}
                                    disabled={researchingAudience || !formData.name || !formData.industry}
                                >
                                    {researchingAudience ? <Loader className="animate-spin" size={16} /> : <><Sparkles size={16} /> Research</>}
                                </button>
                            </div>

                            {/* Feedback/Action buttons after research */}
                            {audienceResearched && !showFeedbackInput && (
                                <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={handleAcceptAudience}
                                        style={{ flex: 1, background: 'var(--color-success)', color: 'white' }}
                                    >
                                        <ThumbsUp size={16} /> Looks Good
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleRequestChanges}
                                        style={{ flex: 1 }}
                                    >
                                        <ThumbsDown size={16} /> Request Changes
                                    </button>
                                </div>
                            )}

                            {/* Feedback input */}
                            {showFeedbackInput && (
                                <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-primary)' }}>
                                    <label className="input-label" style={{ marginBottom: 'var(--space-2)' }}>What would you like to change?</label>
                                    <textarea
                                        className="input-field"
                                        placeholder="e.g., 'Focus more on B2B audience', 'Target younger demographics', 'Include global markets'"
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                        rows={3}
                                        style={{ marginBottom: 'var(--space-2)' }}
                                    />
                                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleRegenerateWithFeedback}
                                            disabled={!feedbackText.trim() || researchingAudience}
                                            style={{ flex: 1 }}
                                        >
                                            {researchingAudience ? <Loader className="animate-spin" size={16} /> : <><RefreshCw size={16} /> Regenerate</>}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => { setShowFeedbackInput(false); setFeedbackText(''); }}
                                        >
                                            <X size={16} /> Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="input-group" style={{ marginBottom: 'var(--space-3)' }}>
                                <label className="input-label">Demographics</label>
                                <textarea
                                    className="input-field"
                                    placeholder="e.g., 25-45 year old professionals, urban areas"
                                    value={formData.audience.demographics}
                                    onChange={(e) => setFormData({ ...formData, audience: { ...formData.audience, demographics: e.target.value } })}
                                    rows={2}
                                />
                            </div>

                            <div className="input-group" style={{ marginBottom: 'var(--space-3)' }}>
                                <label className="input-label">Interests</label>
                                <textarea
                                    className="input-field"
                                    placeholder="e.g., Technology, sustainability, fitness"
                                    value={formData.audience.interests}
                                    onChange={(e) => setFormData({ ...formData, audience: { ...formData.audience, interests: e.target.value } })}
                                    rows={2}
                                />
                            </div>

                            <div className="input-group">
                                <label className="input-label">Pain Points</label>
                                <textarea
                                    className="input-field"
                                    placeholder="e.g., Time constraints, budget limitations"
                                    value={formData.audience.painPoints}
                                    onChange={(e) => setFormData({ ...formData, audience: { ...formData.audience, painPoints: e.target.value } })}
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? <Loader className="animate-spin" size={16} /> : <><Check size={16} /> {editingBrand ? 'Update' : 'Create'} Brand</>}
                        </button>
                    </div>
                </form>
            </div>

            {/* Unsaved Changes Confirmation Modal */}
            <ConfirmationModal
                isOpen={showUnsavedModal}
                onClose={() => setShowUnsavedModal(false)}
                onConfirm={handleDiscardAndClose}
                title="Unsaved Changes"
                message="You have unsaved changes. Are you sure you want to close without saving?"
                confirmText="Discard Changes"
                cancelText="Keep Editing"
                variant="danger"
            />
        </>
    );
};

export default BrandEditorSidebar;
